import { useEffect, useState } from "react";
import i18n from "../../config/i18n";
import OauthApiService from "../../services/OauthService";
import EmailSignatureService from "../../services/EmailSignatureService";
import { getEvidenceData } from "../../util/getEvidenceData";
import { Loader } from "../../components/Loader/Loader";
import Helper from "../../util/helper";
import gzip from "gzip-js";
import { useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import SignaturePad from "react-signature-canvas";
import { useTranslation } from "react-i18next";
import { useOrientation } from "./../../hooks/useOrientation";

import { useWebSocket, parseWsPayload } from "../../hooks/useWebSocket";
import { ArrowIcon } from "../../components/SvgComponents/ArrowIcon/ArrowIcon";
import { SignatureCompletionImage } from "../../components/SvgComponents/SignatureCompletionImage/SignatureCompletionImage";
import ThemeService from "../../services/ThemeService";
import { CancelIcon } from "../../components/SvgComponents/CancelIcon/CancelIcon";

import './SignaturePanel.css';

const WS_HUB_ID = process.env.REACT_APP_WS_HUB_ID;

// Reintentos de entrega del grafo. El desktop deduplica por SignatureId, así
// que reenviar es seguro.
const SEND_MAX_ATTEMPTS = 3;
const ACK_TIMEOUT = 2500;
const CONNECT_TIMEOUT = 5000;
const FLUSH_TIMEOUT = 10000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const SignaturePanelView = () => {
  const { isLandscape, isPortrait } = useOrientation();

  const [loading, setLoading] = useState(true);
  const [tokenId, setTokenId] = useState(null);
  const [emailId, setEmailId] = useState(null);
  const [disable, setDisable] = useState(true);
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);

  const { t } = useTranslation();

  const signatureIdRef = useRef(null);
  const graphBodyRef = useRef(null);
  const ackedRef = useRef(false);

  const handleWsMessage = (data) => {
    if (data?.Type !== 3) return;
    const payload = parseWsPayload(data.Message);
    if (payload?.Ack && payload.Ack === signatureIdRef.current) {
      ackedRef.current = true;
    }
  };

  const { disconnect, sendMessage, ensureConnected, waitForFlush } = useWebSocket(
    WS_HUB_ID,
    tokenId,
    2,
    handleWsMessage
  );

  const sigPad = useRef({});

  const [canvasWidth, setCanvasWidth] = useState(null);
  const [canvasHeight, setCanvasHeight] = useState(null);

  const containerRef = useRef();
  const buttonsRef = useRef();

  const isDarkMode = () => {
    return window.matchMedia && 
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const getPenColor = () => {
    if (isDarkMode()) {
      return 'rgb(255, 255, 255)';
    }
    return 'rgb(0, 0, 255)';
  }

  const getToken = async (emailId) => {
    OauthApiService.getToken(emailId)
      .then(() => {
        ThemeService.asyncSetTheme(emailId)
          .then(() => {
            setLoading(false);
          })
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      })
  };

  const decodeToken = (token) => {
    const tokenParts = token.split(",");
    const jsonTokenInfo = atob(tokenParts[0]);
    const tokenInfo = JSON.parse(jsonTokenInfo);

    return tokenInfo;
  };

  useEffect(() => {
    const urlString = window.location.href;
    const url = new URL(urlString);
    const qrToken = url.searchParams.get("token");

    const tokenInfo = decodeToken(qrToken);
    const { EmailId, TokenId, Language } = tokenInfo;
    i18n.changeLanguage(Language);
    setTokenId(TokenId);
    setEmailId(EmailId);

    getToken(EmailId);
  }, []);

  const sendGraphData = async () => {
    setDisable(true);
    let points;
    let image;

    const canvas = sigPad.current.getCanvas();
    const ctx = canvas.getContext("2d");

    let signatureRawDataZippedAndEncoded = "";

    if (isDarkMode()) {
      const originalImage = canvas.toDataURL();

      // Trim first on the transparent canvas, then transform colors
      const trimmedCanvas = sigPad.current.getTrimmedCanvas();
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = trimmedCanvas.width;
      tempCanvas.height = trimmedCanvas.height;
      const tempCtx = tempCanvas.getContext("2d");

      tempCtx.drawImage(trimmedCanvas, 0, 0);
      // Change white signature to blue
      tempCtx.globalCompositeOperation = "source-atop";
      tempCtx.fillStyle = "blue";
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      // Add white background
      tempCtx.globalCompositeOperation = "destination-over";
      tempCtx.fillStyle = "white";
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      image = tempCanvas.toDataURL();

      const img = new Image();
      img.src = originalImage;
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
    } else {
      image = sigPad.current.getTrimmedCanvas().toDataURL();
    }
      // 🔹 Generar los datos de la firma
    points = Helper.computePressureForSignaturePoints(sigPad.current.toData());
    const timestampStart = points[0][0].time;

    const signatureRawDataStr = points.reduce((str, element) => {
      const stroke = element.reduce((str, rd, i) => {
        const time = rd.time - timestampStart;
        const { x, y, pressure } = rd;
        const letter = i === 0 ? "U" : "T";
        return `${str}${time}:${x}:${y}:${pressure}:${letter};`;
      }, "");
      return `${str}${stroke}`;
    }, "");

    signatureRawDataZippedAndEncoded = Helper.base64ArrayBuffer(
      gzip.zip(signatureRawDataStr)
    );

    let signatureImageRequest = image.substr(22);

    const evidenceData = await getEvidenceData();
    const evidenceBody = {
      EvidenceIP: evidenceData.IpAddress,
      EvidenceLocation: evidenceData.Location,
      From: evidenceData.From,
      EvidenceType: 'QR_SIGN',
    };
    EmailSignatureService.sendEvidence(emailId, evidenceBody);

    signatureIdRef.current = crypto.randomUUID();

    const body = {
      SignatureImage: signatureImageRequest,
      SignatureRawData: signatureRawDataZippedAndEncoded,
      SignatureId: signatureIdRef.current
    };
    graphBodyRef.current = body;

    await deliverGraph(body);
  };

  const waitForAck = (timeout) =>
    new Promise((resolve) => {
      if (ackedRef.current) {
        resolve(true);
        return;
      }
      const deadline = Date.now() + timeout;
      const interval = setInterval(() => {
        if (ackedRef.current) {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() >= deadline) {
          clearInterval(interval);
          resolve(false);
        }
      }, 100);
    });

  // Sin persistencia en servidor, la única confirmación real de entrega es el
  // ACK que devuelve el desktop. Reintentamos hasta conseguirlo.
  const deliverGraph = async (body) => {
    setSending(true);
    setSendError(false);
    ackedRef.current = false;

    let flushedOnce = false;

    for (let attempt = 1; attempt <= SEND_MAX_ATTEMPTS; attempt++) {
      const connected = await ensureConnected(CONNECT_TIMEOUT);
      if (!connected) {
        await wait(1000 * attempt);
        continue;
      }

      if (!sendMessage(body)) {
        await wait(1000 * attempt);
        continue;
      }

      // Cerrar el socket antes de que el navegador vacíe el buffer perdía el
      // payload en redes móviles lentas.
      const flushed = await waitForFlush(FLUSH_TIMEOUT);
      if (!flushed) {
        await wait(1000 * attempt);
        continue;
      }
      flushedOnce = true;

      if (await waitForAck(ACK_TIMEOUT)) {
        finishDelivery(true);
        return;
      }

      console.warn(`Graph sent (attempt ${attempt}) but no ACK from desktop yet`);
    }

    if (flushedOnce) {
      // El grafo salió del dispositivo pero nadie lo confirmó. No podemos
      // asegurar que el desktop lo tenga; damos por bueno el envío para no
      // bloquear al firmante, pero queda registrado para diagnóstico.
      console.warn("Graph delivered without ACK confirmation");
      finishDelivery(true);
      return;
    }

    finishDelivery(false);
  };

  const finishDelivery = (delivered) => {
    setSending(false);
    if (!delivered) {
      setSendError(true);
      setDisable(false);
      return;
    }
    setSuccess(true);
    closeConnection();
  };

  const closeConnection = async () => {
    await waitForFlush(FLUSH_TIMEOUT);
    disconnect();
  };

  const retrySend = () => {
    if (!graphBodyRef.current) return;
    // Reutilizamos el mismo SignatureId para que un reintento nunca duplique
    // la firma en el desktop.
    deliverGraph(graphBodyRef.current);
  };

  const handleDraw = () => {
    setDisable(false);

    const canvas = sigPad.current.getCanvas();
    if (canvas) {
      canvas.classList.remove('canvas-with-image');
    }
  };

  const clear = () => {
    if (typeof(sigPad?.current?.clear) === 'function') {
      sigPad.current.clear();
      setDisable(true);

      const canvas = sigPad.current.getCanvas();
      if (canvas) {
        canvas.classList.add('canvas-with-image');
      }
    }
  };

  const getWidthAndHeightContainers = () => {
    let width = loading ? window.screen.width : containerRef?.current?.clientWidth;
    width = width - 40;
    const height = isPortrait ? 418 : 200;

    setCanvasWidth(width);
    setCanvasHeight(height);
  };

  useEffect(() => {
    getWidthAndHeightContainers();
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandscape, isPortrait, success, loading]);

  if (loading || sending) return <Loader />;

  if (sendError) {
    return (
      <Container className="container-row row-box">
        <Row>
          <Col md={2}></Col>
          <Col md={8} xs={12} className="justify-content-center container-box success-qr-container-box">
            <Row>
              <h2 className="rejected-title">{t("sign.qr.send_error_title")}</h2>
              <p className="rejected-subtitle">{t("sign.qr.send_error_description")}</p>
              <button
                type="button"
                className="btn btn-primary btn-active button-canvas"
                onClick={() => retrySend()}
              >
                <span className="cancel-text">{t("sign.qr.send_retry")}</span>
                <ArrowIcon />
              </button>
            </Row>
          </Col>
          <Col md={2}></Col>
        </Row>
      </Container>
    );
  }

  return (
    success ? <Container className="container-row row-box">
      <Row>
        <Col md={2}></Col>
        <Col md={8} xs={12} className="justify-content-center container-box success-qr-container-box">
          <Row>
            <SignatureCompletionImage />
            <h2 className="rejected-title">{t("sign.sign_correct")}</h2>
            <p className="rejected-subtitle">{t("sign.finish_process_computer")}</p>
          </Row>
        </Col>
        <Col md={2}></Col>
      </Row>
    </Container> :
      <Row className="signature-panel-container" ref={containerRef}>
        <Col md={12} lg={12}>
          <Row className="document-preview-title title-sign">
            <Col md={5} sm={5} className="document-preview-title title-sign">
              <span className="signer-name"><strong> {t('sign.draw_signature')} </strong> </span>
            </Col>
            {
              isLandscape &&
              <Col md={7} lg={7} xs={7} className='landscape-buttons-container'>
                <button
                  type="button"
                  className="btn btn-secondary btn-cancel button-canvas"
                  onClick={() => clear()}
                >
                  <Row>
                    <Col>{t("sign.erase")}</Col>
                    <Col className="text-align-end">
                      <CancelIcon />
                    </Col>
                  </Row>
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-active button-canvas"
                  disabled={disable}
                  onClick={() => sendGraphData()}
                >
                  <Row>
                    <Col>{t("general.finish")}</Col>
                    <Col className="text-align-end">
                      <ArrowIcon disabled={disable} />
                    </Col>
                  </Row>
                </button>
              </Col>
            }
          </Row>
          <SignaturePad
            id="sp"
            penColor={getPenColor()}
            canvasProps={{ width: canvasWidth, height: canvasHeight, className: 'signatureCanvas' }}
            ref={sigPad}
            onBegin={() => handleDraw()}
            onEnd={() => handleDraw()}
          />
          {isPortrait && <Row className="action-buttons mobile-button" ref={buttonsRef}>
            <Col md={6} lg={6} xs={6}>
              <button
                type="button"
                className="btn btn-secondary btn-cancel button-canvas"
                onClick={() => clear()}
              >
                <span className="cancel-text">{t("sign.erase")}</span>
                <CancelIcon />
              </button>
            </Col>
            <Col md={6} lg={6} xs={6}>
              <button
                type="button"
                className="btn btn-primary btn-active button-canvas"
                disabled={disable}
                onClick={() => sendGraphData()}
              >
                <span className="cancel-text">{t("general.finish")}</span>
                <ArrowIcon disabled={disable} />
              </button>
            </Col>
            <Col md={1} lg={1}></Col>
          </Row>}
        </Col>
      </Row>
  );
};
